import { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/sonner";
import {
  Play, Pause, Download, FolderDown, Disc3, Music2, Check, Loader2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

function formatSec(s) {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}

export default function MyCollection({
  albums,
  mirrorsId,
  onRefresh,
  atmosEnabled,
  setupAtmos,
  cleanupAtmos,
}) {
  const audioRef = useRef(null);
  const progressInterval = useRef(null);
  const [playingTrackId, setPlayingTrackId] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [trackProgress, setTrackProgress] = useState(0);
  const [trackDuration, setTrackDuration] = useState(0);
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadingAlbum, setDownloadingAlbum] = useState(null);
  const [expandedAlbum, setExpandedAlbum] = useState(albums[0]?.key || null);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if (progressInterval.current) clearInterval(progressInterval.current);
    if (cleanupAtmos) cleanupAtmos();
    setPlayingTrackId(null);
    setIsPaused(false);
    setTrackProgress(0);
    setTrackDuration(0);
  }, [cleanupAtmos]);

  const startProgress = () => {
    if (progressInterval.current) clearInterval(progressInterval.current);
    progressInterval.current = setInterval(() => {
      if (audioRef.current) {
        setTrackProgress(audioRef.current.currentTime);
        setTrackDuration(audioRef.current.duration || 0);
      }
    }, 250);
  };

  const playTrack = (track, albumKey) => {
    if (!track?.has_audio) return;

    const fullId = `${albumKey}-${track.id}`;
    if (playingTrackId === fullId && !isPaused) {
      audioRef.current?.pause();
      setIsPaused(true);
      if (progressInterval.current) clearInterval(progressInterval.current);
      return;
    }
    if (playingTrackId === fullId && isPaused) {
      audioRef.current?.play();
      setIsPaused(false);
      startProgress();
      return;
    }

    stopAudio();
    const url = `${API}/collection/download/${mirrorsId}/${albumKey}/${track.id}`;
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.volume = 0.8;
    if (atmosEnabled && setupAtmos) setupAtmos(audio);
    audio.play();
    setPlayingTrackId(fullId);
    setIsPaused(false);

    audio.addEventListener("loadedmetadata", () => setTrackDuration(audio.duration));
    audio.addEventListener("ended", () => {
      setPlayingTrackId(null);
      setIsPaused(false);
      setTrackProgress(0);
      if (progressInterval.current) clearInterval(progressInterval.current);
    });
    startProgress();
  };

  const seekTrack = (e, track, albumKey) => {
    const fullId = `${albumKey}-${track.id}`;
    if (!audioRef.current || playingTrackId !== fullId) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = pct * (audioRef.current.duration || 0);
  };

  const downloadTrack = async (track, albumKey) => {
    const dlId = `${albumKey}-${track.id}`;
    setDownloadingId(dlId);
    try {
      const url = `${API}/collection/download/${mirrorsId}/${albumKey}/${track.id}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = track.filename;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success(`Downloading ${track.title}`);
    } catch {
      toast.error("Download failed");
    } finally {
      setTimeout(() => setDownloadingId(null), 1500);
    }
  };

  const downloadAlbum = async (album) => {
    setDownloadingAlbum(album.key);
    toast.message(`Preparing ${album.title} download...`);
    try {
      const url = `${API}/collection/download-album/${mirrorsId}/${album.key}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = `${album.title}_-_GODMODE_SOUND.zip`;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      toast.error("Album download failed");
    } finally {
      setTimeout(() => setDownloadingAlbum(null), 3000);
    }
  };

  if (albums.length === 0) {
    return (
      <Card className="bg-[#0B0004]/70 border-[#FF0000]/20">
        <CardContent className="p-8 text-center">
          <Music2 className="h-12 w-12 mx-auto mb-4" style={{ color: "rgba(255,0,0,0.3)" }} />
          <div className="text-lg font-bold font-mono" style={{ color: "rgba(255,0,0,0.7)" }}>
            YOUR COLLECTION IS EMPTY
          </div>
          <div className="text-sm mt-2 font-mono" style={{ color: "rgba(255,0,0,0.45)" }}>
            Purchase albums from the STORE tab to build your collection.
            <br />All purchased music can be played and downloaded directly to your phone.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="my-collection">
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-widest uppercase font-mono" style={{ color: "rgba(255,0,0,0.6)" }}>
          {albums.length} album{albums.length !== 1 ? "s" : ""} &middot; {albums.reduce((s, a) => s + a.track_count, 0)} tracks owned
        </div>
      </div>

      {albums.map((album) => {
        const isExpanded = expandedAlbum === album.key;
        const hasDownloadable = album.tracks.some((t) => t.has_audio);

        return (
          <Card
            key={album.key}
            className="bg-[#0B0004]/70 border-[#FF0000]/20 overflow-hidden"
            data-testid={`collection-album-${album.key}`}
          >
            <CardContent className="p-0">
              {/* Album header */}
              <div
                className="flex items-center gap-3 p-4 cursor-pointer hover:bg-[#FF0000]/5 transition-colors"
                onClick={() => setExpandedAlbum(isExpanded ? null : album.key)}
                data-testid={`collection-header-${album.key}`}
              >
                {album.cover_art_url ? (
                  <img
                    src={album.cover_art_url}
                    alt={album.title}
                    className="w-14 h-14 object-cover shrink-0"
                    style={{ border: "1px solid rgba(255,0,0,0.25)" }}
                  />
                ) : (
                  <div className="w-14 h-14 flex items-center justify-center shrink-0" style={{ border: "1px solid rgba(255,0,0,0.15)", background: "rgba(255,0,0,0.04)" }}>
                    <Disc3 className="h-6 w-6" style={{ color: "rgba(255,0,0,0.5)" }} />
                  </div>
                )}

                <div className="min-w-0 flex-1">
                  <div className="text-base font-bold font-mono truncate" style={{ color: "#FF0000" }}>
                    {album.title}
                  </div>
                  <div className="text-[10px] tracking-widest uppercase font-mono" style={{ color: "rgba(255,0,0,0.5)" }}>
                    {album.artist} &middot; {album.track_count} tracks
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <Badge className="bg-[#FF0000]/10 text-[#FF0000] border border-[#FF0000]/30 text-[9px] font-mono">
                    <Check className="h-3 w-3 mr-1" /> OWNED
                  </Badge>
                  {hasDownloadable && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-[#FF0000]/40 text-[#FF0000] hover:bg-[#FF0000]/10 text-[10px] font-mono"
                      onClick={(e) => { e.stopPropagation(); downloadAlbum(album); }}
                      disabled={downloadingAlbum === album.key}
                      data-testid={`download-all-${album.key}`}
                    >
                      {downloadingAlbum === album.key ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      ) : (
                        <FolderDown className="h-3.5 w-3.5 mr-1" />
                      )}
                      {downloadingAlbum === album.key ? "ZIPPING..." : "DOWNLOAD ALL"}
                    </Button>
                  )}
                </div>
              </div>

              {/* Track list (expanded) */}
              {isExpanded && (
                <div className="border-t border-[#FF0000]/10">
                  {album.tracks.length === 0 ? (
                    <div className="p-4 text-xs font-mono" style={{ color: "rgba(255,0,0,0.4)" }}>
                      Tracks will be available for streaming and download soon.
                    </div>
                  ) : (
                    <div className="divide-y divide-[#FF0000]/8">
                      {album.tracks.map((t) => {
                        const fullId = `${album.key}-${t.id}`;
                        const isPlaying = playingTrackId === fullId && !isPaused;
                        const isThis = playingTrackId === fullId;
                        const pct = isThis && trackDuration > 0 ? (trackProgress / trackDuration) * 100 : 0;
                        const dlId = `${album.key}-${t.id}`;

                        return (
                          <div
                            key={t.id}
                            className="px-4 py-3 space-y-1.5"
                            style={{ background: isThis ? "rgba(255,0,0,0.04)" : "transparent" }}
                            data-testid={`collection-track-${t.id}`}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0 flex items-center gap-2">
                                <div className="text-xs w-5 font-mono text-right shrink-0" style={{ color: "rgba(255,0,0,0.4)" }}>
                                  {t.track_number}
                                </div>
                                <div className="truncate text-sm font-bold font-mono" style={{ color: "#FF0000" }}>
                                  {t.title}
                                </div>
                                {isThis && (
                                  <span className="text-[10px] font-mono shrink-0" style={{ color: "rgba(255,0,0,0.5)" }}>
                                    {formatSec(trackProgress)} / {formatSec(trackDuration)}
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center gap-1.5 shrink-0">
                                {t.has_audio && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="border-[#FF0000]/40 text-[#FF0000] hover:bg-[#FF0000]/10 h-8 px-2.5"
                                      onClick={() => playTrack(t, album.key)}
                                      data-testid={`play-collection-${t.id}`}
                                    >
                                      {isPlaying ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                                    </Button>
                                    <Button
                                      size="sm"
                                      className="bg-[#FF0000]/15 text-[#FF0000] hover:bg-[#FF0000]/25 border border-[#FF0000]/30 h-8 px-2.5"
                                      onClick={() => downloadTrack(t, album.key)}
                                      disabled={downloadingId === dlId}
                                      data-testid={`download-track-${t.id}`}
                                    >
                                      {downloadingId === dlId ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                      ) : (
                                        <Download className="h-3.5 w-3.5" />
                                      )}
                                    </Button>
                                  </>
                                )}
                                {!t.has_audio && (
                                  <span className="text-[9px] font-mono" style={{ color: "rgba(255,0,0,0.3)" }}>COMING SOON</span>
                                )}
                              </div>
                            </div>

                            {isThis && (
                              <div
                                className="w-full h-1 bg-[#FF0000]/10 cursor-pointer ml-7"
                                style={{ width: "calc(100% - 1.75rem)" }}
                                onClick={(e) => seekTrack(e, t, album.key)}
                                data-testid={`progress-collection-${t.id}`}
                              >
                                <div className="h-full bg-[#FF0000] transition-none" style={{ width: `${pct}%` }} />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      <div className="text-[9px] font-mono text-center mt-2" style={{ color: "rgba(255,0,0,0.3)" }}>
        Tap an album to expand &middot; Play in-browser or download to your device
      </div>
    </div>
  );
}
